import { createTheme } from '@mui/material/styles';
import { colors, semanticColors } from './colors';
import { typography } from './typography';
import './theme-types';

/**
 * Custom Material-UI theme for Dataloop Asset Management Admin Portal
 *
 * Colors are extracted from Figma design system and defined in colors.ts
 * Typography uses Roboto font family defined in typography.ts
 *
 */
const theme = createTheme({
	palette: {
		primary: {
			main: semanticColors.primaryMain,
			light: semanticColors.primaryLight,
			dark: semanticColors.primaryDark,
			contrastText: '#ffffff',
		},
		secondary: {
			main: colors.primary['300'],
			light: colors.primary['200'],
			dark: colors.primary['400'],
			contrastText: '#ffffff',
		},
		error: {
			main: semanticColors.error,
			light: semanticColors.errorLight,
			dark: colors.error['500'],
			contrastText: '#ffffff',
		},
		warning: {
			main: semanticColors.warning,
			light: semanticColors.warningLight,
			dark: colors.warning['500'],
			contrastText: '#ffffff',
		},
		success: {
			main: semanticColors.success,
			light: semanticColors.successLight,
			dark: colors.success['500'],
			contrastText: '#ffffff',
		},
		info: {
			main: colors.primary['400'],
			light: colors.primary['300'],
			dark: colors.primary['500'],
			contrastText: '#ffffff',
		},
		background: {
			default: semanticColors.backgroundDefault,
			paper: semanticColors.backgroundPaper,
		},
		text: {
			primary: semanticColors.textPrimary,
			secondary: semanticColors.textSecondary,
			disabled: semanticColors.textDisabled,
		},
		divider: colors.neutrals['outline'],
		grey: {
			50: colors.neutrals['bg-light-colored'],
			100: colors.neutrals['bg-active'],
			200: colors.neutrals['outline'],
			300: colors.neutrals['gray-disabled'],
			400: colors.neutrals['gray-light'],
			500: colors.neutrals['gray-medium'],
			600: colors.neutrals['gray-medium'],
			700: colors.neutrals['gray-dark'],
			800: colors.neutrals['gray-dark'],
			900: colors.neutrals['dark-base'],
		},
	},
	typography,
	spacing: 8, // 8px base spacing unit
	shape: {
		borderRadius: 4,
	},
	components: {
		MuiCssBaseline: {
			styleOverrides: {
				body: {
					fontFamily: typography.fontFamily,
					backgroundColor: semanticColors.backgroundDefault,
					color: semanticColors.textPrimary,
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					fontFamily: typography.fontFamily,
					textTransform: 'none',
				},
			},
		},
		MuiTextField: {
			defaultProps: {
				size: 'small',
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					'&.Mui-checked': {
						color: colors.primary['500'],
					},
					'&.MuiCheckbox-indeterminate': {
						color: colors.primary['500'],
					},
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'&.Mui-selected': {
						backgroundColor: colors.primary['100'],
						'&:hover': {
							backgroundColor: colors.primary['100'],
						},
					},
				},
			},
		},
	},
});

export default theme;
